import { useTranslation } from 'react-i18next'
import type React from 'react'
import { Building2, ExternalLink, Trash2, ChevronRight, CreditCard, Link2, Unplug, Users } from 'lucide-react'
import { SectionSurface } from './settings-primitives'

// Cross-app deep link to my.wetook subscription page. Mirrors the env-var
// pattern Sidebar/TopBar already use; falls back to the relative path so
// dev (single-host) and prod (subdomained) both work without code changes.
const MY_APP_URL = ((import.meta as any).env?.VITE_MY_URL as string || '').replace(/\/$/, '')
const subscriptionUrl = (appKind = 'signage') =>
  MY_APP_URL ? `${MY_APP_URL}/subscription/app/${appKind}` : `/subscription/app/${appKind}`

// ManagementColumn — the "Management" tab of the Screen page. Groups the
// subscription/license controls with the destructive Danger Zone (Transfer /
// Delete), which used to live at the bottom of the Screen Info tab. Splitting
// them out keeps the info tab focused on identity/hardware and gives the
// heavier lifecycle actions their own space.
export default function ManagementColumn({
  onDelete, onTransfer, onRelink, onDetach, hasDevice, currentGroupName, onManageGroup, commands,
}: {
  onDelete:   () => void
  onTransfer: () => void
  /** Swap the hardware, keeping this screen and its content. */
  onRelink:   () => void
  /** Release the hardware, keeping this screen and its content. */
  onDetach:   () => void
  /** False once nothing is attached — Detach has nothing to do. */
  hasDevice:  boolean
  currentGroupName: string | null
  onManageGroup: () => void
  commands: React.ReactNode
}) {
  const { t } = useTranslation()

  return (
    <div className="space-y-5">
      <section>
        <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{t('screens.groups', 'Groups')}</p>
        <SectionSurface className="flex items-center gap-3 px-4 py-3">
          <Users className="w-4 h-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-normal">{currentGroupName || t('screens.noGroupAssigned', 'No group assigned.')}</p>
          </div>
          <button type="button" onClick={onManageGroup} className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground" aria-label={t('common.change', 'Change')}>
            <ChevronRight className="h-4 w-4 rtl:rotate-180" />
          </button>
        </SectionSurface>
      </section>

      {commands}

      {/* Device — the hardware attached to this screen, as opposed to the
          screen itself. Relink lives here rather than in the header dropdown
          it used to hide in: replacing a panel is a management action, and the
          operator looking for it is on this tab, next to Transfer and Delete.
          Not in the Danger Zone — neither action loses anything. */}
      <div>
        <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-1.5">
          {t('screens.deviceAssociation', 'Device association')}
        </p>
        <SectionSurface className="divide-y divide-border overflow-hidden">
          <button type="button" onClick={onRelink} className="flex w-full items-center gap-3 px-4 py-3 text-start transition-colors hover:bg-muted/35">
            <Link2 className="w-4 h-4 text-muted-foreground" />
            <span className="flex-1 text-sm font-normal">{t('screens.relinkDevice', 'Relink device')}</span>
            <ChevronRight className="w-4 h-4 text-muted-foreground rtl:rotate-180" />
          </button>
          <button
            type="button" onClick={onDetach} disabled={!hasDevice}
            className="flex w-full items-center gap-3 px-4 py-3 text-start transition-colors hover:bg-muted/35 disabled:cursor-not-allowed disabled:opacity-50"
            title={hasDevice ? undefined : t('screens.detachNone', 'No device is attached to this screen.')}
          >
            <Unplug className="w-4 h-4 text-muted-foreground" />
            <span className="flex-1 text-sm font-normal">{t('screens.detachDevice', 'Detach device')}</span>
            <ChevronRight className="w-4 h-4 text-muted-foreground rtl:rotate-180" />
          </button>
        </SectionSurface>
      </div>

      {/* Subscription — the same flat surface row as Groups and Device
          association above. It was the one section rendered as a filled
          <Button>, which made a plain navigation link look heavier than the
          Detach and Relink actions sitting right above it. */}
      <div>
        <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-1.5">{t('screens.subscriptionManagement', 'Subscription')}</p>
        <SectionSurface className="overflow-hidden">
          <a
            href={subscriptionUrl('signage')}
            target={MY_APP_URL ? '_blank' : undefined}
            rel="noreferrer"
            className="flex w-full items-center gap-3 px-4 py-3 text-start transition-colors hover:bg-muted/35"
          >
            <CreditCard className="w-4 h-4 shrink-0 text-muted-foreground" />
            <span className="flex-1 text-sm font-normal">{t('screens.manageSubscription', 'Manage Subscription')}</span>
            <span className="text-xs text-muted-foreground">1 {t('screens.licenseUnit', 'License')}</span>
            <ExternalLink className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
          </a>
        </SectionSurface>
      </div>

      {/* Danger zone, flattened. It used to be a red-tinted card wrapping a
          second white card — a fill, a tint and two borders to say what the
          red label and red action text already say. The colour now lives only
          where it carries meaning (the label and the actions themselves), so
          the section reads as a peer of Groups / Device association rather
          than a warning poster stapled to the bottom of the tab. */}
      <section>
        <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-destructive">{t('screens.dangerZone', 'Danger Zone')}</p>
        <SectionSurface className="grid grid-cols-1 overflow-hidden sm:grid-cols-2 sm:divide-x sm:divide-[hsl(var(--border-control))] rtl:sm:divide-x-reverse">
          <button type="button" onClick={onTransfer} className="flex h-12 items-center gap-3 px-4 text-start text-sm font-normal text-destructive transition-colors hover:bg-destructive/5">
            <Building2 className="h-4 w-4 shrink-0" />
            <span className="flex-1">{t('screens.transferScreen', 'Transfer to branch')}</span>
            <ChevronRight className="h-4 w-4 rtl:rotate-180" />
          </button>
          <button type="button" onClick={onDelete} className="flex h-12 items-center gap-3 border-t border-[hsl(var(--border-control))] px-4 text-start text-sm font-normal text-destructive transition-colors hover:bg-destructive/5 sm:border-t-0">
            <Trash2 className="h-4 w-4 shrink-0" />
            <span className="flex-1">{t('screens.deleteScreen', 'Remove screen')}</span>
            <ChevronRight className="h-4 w-4 rtl:rotate-180" />
          </button>
        </SectionSurface>
      </section>
    </div>
  )
}
